import { FC, MouseEvent, useCallback, useMemo, useRef, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { v4 as uuid } from 'uuid'
import { FormGenerator } from '@microservices/wiskey-react-components'
import { MuiEvent } from '@microservices/wiskey-react-table-component'
import { Box, Typography } from '@mui/material'
import {
  BarPlot,
  ChartsAxisHighlight,
  ChartsLegend,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  ResponsiveChartContainer,
} from '@mui/x-charts'

import useBarDiagram from '@pages/BarDiagramPage/hooks/useBarDiagram'
import { useViewFilter } from '@pages/BarDiagramPage/hooks/useViewFilter'
import {
  ConfiguredEntityInitialDataWrapper,
  GetConfiguredEntityParamsHandle,
} from '@pages/ConfiguredEntity'
import { OpenFormProps } from '@pages/ConfiguredEntity/types'

import { PageContentLayout } from '@layouts/index'
import { PageTitle } from '@components/PageTitle'
import { RedirectToConfigButton } from '@components/RedirectToConfigButton'

import { openFormDialog } from '@redux/reducers/dialogWindowManager.reducer'

import { useAppDispatch } from '@hooks'
import { getInitialPositionWindow } from '@helpers'
import { ENTITY, FLEXIBLE, ROUTES } from '@constants'
import { DIALOG_WINDOW_TYPE, EntityType } from '@types'

import { AxisTickLabel } from './components/AxisTickLabel'
import { BarLabel } from './components/BarLabel'
import { LoadingOverlay } from './components/LoadingOverlay'
import { ViewFilterChips } from './components/ViewFilterChips'

const BarDiagramPage: FC = () => {
  const { id } = useParams()
  const { state, handlers, inputs, methods } = useBarDiagram()
  const dialogId = useMemo(() => uuid(), [])

  const dispatch = useAppDispatch()

  const {
    diagramDataSet,
    isLoading,
    columns,
    selectedBar,
    watchFromDate,
    watchDepartment,
    watchQual,
    diagramConfig,
    watchShowFormatDate,
    watchPosition,
    watchGroup,
  } = state

  const { handleBarItemClick } = handlers

  const { filterInputs } = inputs

  const viewRef = useRef<GetConfiguredEntityParamsHandle>(null)

  const [entity, setEntity] = useState<EntityType | null>(null)

  const { viewFilter, setViewFilter } = useViewFilter({
    selectedBar,
    watchFromDate,
    watchDepartment,
    watchQual,
    watchPosition,
    watchGroup,
  })

  const xAxisLabelBySeries = useMemo(() => {
    return new Map(
      diagramDataSet.map((item: any) => [
        String(item.seriesTitle),
        String(item.formattedDate ?? item.seriesTitle),
      ])
    )
  }, [diagramDataSet])

  const xAxisTickStep = useMemo(() => {
    const itemsCount = diagramDataSet.length
    if (itemsCount <= 12) return 1
    if (itemsCount <= 24) return 2
    if (itemsCount <= 36) return 3

    return Math.max(1, Math.ceil(itemsCount / 12))
  }, [diagramDataSet.length])

  function handleOpenForm(
    formProps: OpenFormProps,
    event: MouseEvent<HTMLButtonElement> | MuiEvent<MouseEvent>
  ) {
    const { id } = formProps

    const initialPosition = getInitialPositionWindow(event)

    dispatch(
      openFormDialog({
        id,
        parentDialogId: null,
        type: DIALOG_WINDOW_TYPE.FORM,
        title: null,
        meta: {
          ...formProps,
          globalId: id,
        },
        initialPosition,
      })
    )
  }

  const handleSetEntity = useCallback((value: EntityType | null) => setEntity(value), [])

  return (
    <>
      <PageTitle
        end={false}
        start={
          <>
            <Typography display={'inline'} sx={{ mr: 1, ml: 1 }} variant={'h6'}>
              {diagramConfig?.title}
            </Typography>
          </>
        }
      />
      <Box p={2}>
        <FormProvider {...methods}>
          <FormGenerator inputs={filterInputs} mainGridSpacing={0} />
        </FormProvider>
      </Box>
      <Box display={'flex'} justifyContent={'flex-end'} pr={2}>
        <RedirectToConfigButton code={id} type='diagram' />
      </Box>
      <PageContentLayout sx={{ ...FLEXIBLE, p: 1 }}>
        <Box sx={{ minHeight: 300, flex: 1 }}>
          <ResponsiveChartContainer
            skipAnimation
            dataset={diagramDataSet}
            margin={{ top: 48, right: 16, bottom: 84, left: 40 }}
            series={columns as any}
            xAxis={[
              {
                tickLabelInterval: () => true,
                scaleType: 'band',
                dataKey: 'seriesTitle',
                tickLabelStyle: { fontSize: 10 },
                valueFormatter: value =>
                  watchShowFormatDate
                    ? xAxisLabelBySeries.get(String(value)) ?? String(value)
                    : String(value),
              },
            ]}
          >
            <BarPlot
              barLabel={'value'}
              slots={{
                barLabel: BarLabel,
              }}
              onItemClick={handleBarItemClick}
            />
            <ChartsXAxis
              slots={{
                axisTickLabel: (props: any) => (
                  <AxisTickLabel {...props} diagramDataSet={diagramDataSet} />
                ),
              }}
            />
            <ChartsYAxis min={0} tickMinStep={1} />
            <ChartsAxisHighlight x={'band'} />
            <ChartsTooltip trigger={'axis'} />
            <ChartsLegend
              position={{ vertical: 'top', horizontal: 'middle' }}
              padding={{ top: 4, bottom: 12 }}
              onItemClick={handleBarItemClick}
            />
            {isLoading && <LoadingOverlay />}
          </ResponsiveChartContainer>
        </Box>

        {viewFilter && (
          <ViewFilterChips
            filter={viewFilter}
            handleDeleteFilter={() => setViewFilter(undefined)}
          />
        )}
        {viewFilter && (
          <Box sx={{ minHeight: 400, maxHeight: 500 }}>
            <ConfiguredEntityInitialDataWrapper
              key={viewFilter.entityId}
              ref={viewRef}
              isViewDialogWindow
              defaultFilter={viewFilter.filter}
              dialogId={dialogId}
              entity={entity}
              entityCode={diagramConfig?.columns[0]?.code ?? null}
              entityId={viewFilter.entityId ?? null}
              instanceId={dialogId}
              path={ROUTES.BAR_CHART}
              showQuickSearch={false}
              title={'title'}
              type={ENTITY.VIEW}
              onOpenForm={handleOpenForm}
              onSetEntity={handleSetEntity}
            />
          </Box>
        )}
      </PageContentLayout>
    </>
  )
}

export default BarDiagramPage
